/**
 * LeagueDetailView Component - PadelPulse Stitch Design
 * Dashboard layout showing league metadata, standings, and rounds
 * Uses Stitch stats cards, data tables, and tabbed interface
 */

import { apiService } from "../services/api";
import { LeagueEnrollmentComponent } from "../components/LeagueEnrollmentComponent.js";
import { LeagueSettingsModal } from "../components/LeagueSettingsModal";
import type { LeagueStanding, League, Round } from "../types/index";
import { applyBaseLayout, setupHeaderListeners } from "../layouts/LayoutManager";
import { showNotification, renderLoadingState } from "../utils/ui.js";
import { computePlayerStats, renderStandingRow, EMPTY_STANDINGS_ROW } from "./leagueDetail/standings";
import { renderRoundsList } from "./leagueDetail/roundsList";
import {
  renderLeagueInsights,
  renderLeagueInsightsLoading,
  renderLeagueInsightsError,
} from "./leagueDetail/insights";
import { wireNudgeButtons } from "../components/dormantPlayersCard";

/** Ops Insights is an organizer drill-down; the platform owner never sees it. */
const isManagingOrganizer = (): boolean => localStorage.getItem("userRole") !== "owner";

export class LeagueDetailView {
  private container: HTMLElement;
  private leagueId: number;
  private league: League | null = null;
  private rounds: Round[] = [];
  private standings: LeagueStanding[] = [];
  private settingsModal: LeagueSettingsModal;
  // Ops Insights are fetched lazily the first time the tab is opened, then cached.
  private insightsLoaded = false;

  constructor(container: HTMLElement, leagueId: number) {
    this.container = container;
    this.leagueId = leagueId;
    this.settingsModal = new LeagueSettingsModal(() => this.render()); // Re-render on update
  }

  async render(): Promise<void> {
    // A full re-render rebuilds the DOM, so the Insights panel reverts to its
    // placeholder — let it reload when the tab is next opened.
    this.insightsLoaded = false;
    this.container.innerHTML = this.getLoadingHTML();
    try {
      const [league, standings] = await Promise.all([
        apiService.getLeagueById(this.leagueId),
        apiService.getStandings(this.leagueId)
      ]);

      this.league = league;
      this.standings = Array.isArray(standings) ? standings : (standings as any).data || [];
      this.rounds = (league as any).rounds || [];

      if (!this.league) {
        throw new Error("League data could not be loaded.");
      }

      const contentHTML = this.getDashboardHTML();
      const layoutHTML = applyBaseLayout({
        activePage: "leagues",
        container: this.container,
        content: contentHTML,
        title: this.league.name,
        description: this.league.description
      });

      this.container.innerHTML = layoutHTML;

      this.setupHeader();
      this.setupEventListeners();
      this.renderStandings();
      this.checkScheduleEligibility();

      // Initialize Enrollment Component
      const enrollmentContainer = this.container.querySelector('#enrollment-container') as HTMLElement;
      if (enrollmentContainer) {
        const enrollment = new LeagueEnrollmentComponent(enrollmentContainer, this.leagueId, this.league.mode);
        enrollment.render();
      }

    } catch (error) {
      console.error("Failed to load league details:", error);
      this.container.innerHTML = `
        <div class="flex items-center justify-center min-h-screen">
            <div class="text-center">
                <span class="material-symbols-outlined text-6xl text-error mb-4">error</span>
                <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">Failed to load league details</p>
                <button class="btn-primary mt-4" onclick="window.location.reload()">Retry</button>
            </div>
        </div>
      `;
    }
  }

  private getLoadingHTML(): string {
    return renderLoadingState("Loading league details…");
  }

  private setupHeader(): void {
    setupHeaderListeners(this.container, {
      onLogout: () => {
        localStorage.removeItem("authToken");
        window.location.hash = "#login";
      },
      onCreateLeague: () => {
        // Route home AND open the create-league form (the router handles the
        // "/new" suffix). A bare "#home" would just dump the user on the list.
        window.location.hash = "#home/new";
      }
    });
  }

  private getDashboardHTML(): string {
    if (!this.league) return "";

    const getFormatInfo = (format: string) => {
      switch (format) {
        case 'swiss': return { type: 'Pairs', system: 'Swiss System' };
        case 'roundrobin': return { type: 'Pairs', system: 'Round Robin' };
        case 'individual_swiss': return { type: 'Individual', system: 'Swiss System' };
        case 'individual_roundrobin': return { type: 'Individual', system: 'Round Robin' };
        default: return { type: 'Unknown', system: 'Format' };
      }
    };
    const formatInfo = getFormatInfo(this.league.format);

    return `
          
          <!-- Hero Section -->
          <div class="mb-12">
            <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-8">
              <div>
                <div class="flex items-center gap-3 mb-4">
                  <div class="size-12 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white daylight:text-navy-800 shadow-lg daylight:shadow-day-lg">
                    <span class="material-symbols-outlined" style="font-size: 24px;">trophy</span>
                  </div>
                  <h1 class="text-4xl lg:text-5xl font-black text-slate-900 dark:text-white">${this.league.name}</h1>
                </div>
                <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft text-lg max-w-2xl">${this.league.description || 'League description not available'}</p>
              </div>
              <div class="flex gap-3">
                <button id="league-settings-btn" class="btn-secondary flex items-center gap-2">
                  <span class="material-symbols-outlined">settings</span>
                  <span>Settings</span>
                </button>
              </div>
            </div>

            <!-- League Info Badges -->
            <div class="flex flex-wrap gap-3">
              <span class="badge badge-primary">${formatInfo.type}</span>
              <span class="badge bg-slate-100 dark:bg-slate-800 daylight:bg-day-sunken text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft border border-slate-200 dark:border-slate-700 daylight:border-day-ink/10">${formatInfo.system}</span>
              <span class="badge ${this.league.isActive ? 'badge-success' : 'badge-warning'}">${this.league.isActive ? 'Active' : 'Inactive'}</span>
              <span class="badge badge-primary">Round ${this.rounds.length}</span>
            </div>
          </div>

          <!-- Stats Row -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div class="card">
              <div class="flex items-center justify-between mb-4">
                <div class="p-2 bg-primary/10 daylight:bg-primary/20 rounded-lg text-primary daylight:text-day-ink">
                  <span class="material-symbols-outlined">groups</span>
                </div>
                <span class="badge badge-primary">Active</span>
              </div>
              <p class="text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted text-sm font-medium">Enrolled Players</p>
              <h3 class="text-3xl font-black mt-2">${(this.league as any).enrollments?.length || this.standings.length || 0}</h3>
            </div>

            <div class="card">
              <div class="flex items-center justify-between mb-4">
                <div class="p-2 bg-success/10 rounded-lg text-success">
                  <span class="material-symbols-outlined">calendar_month</span>
                </div>
                <span class="badge badge-success">Completed</span>
              </div>
              <p class="text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted text-sm font-medium">Completed Rounds</p>
              <h3 class="text-3xl font-black mt-2">${this.getCompletedRounds()}</h3>
            </div>

            <div class="card">
              <div class="flex items-center justify-between mb-4">
                <div class="p-2 bg-warning/10 rounded-lg text-warning">
                  <span class="material-symbols-outlined">sports_score</span>
                </div>
                <span class="badge badge-warning">Pending</span>
              </div>
              <p class="text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted text-sm font-medium">Pending Matches</p>
              <h3 class="text-3xl font-black mt-2">${this.getPendingMatches()}</h3>
            </div>

            <div class="card">
              <div class="flex items-center justify-between mb-4">
                <div class="p-2 bg-primary/10 daylight:bg-primary/20 rounded-lg text-primary daylight:text-day-ink">
                  <span class="material-symbols-outlined">trending_up</span>
                </div>
                <span class="badge badge-primary">Current</span>
              </div>
              <p class="text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted text-sm font-medium">Current Round</p>
              <h3 class="text-3xl font-black mt-2">${this.rounds.length > 0 ? this.rounds[this.rounds.length - 1].roundNumber : '-'}</h3>
            </div>
          </div>

          <!-- Tabbed Interface -->
          <div class="bg-white dark:bg-surface-dark daylight:bg-day-card rounded-2xl border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 overflow-hidden shadow-sm daylight:shadow-day-sm">
            <!-- Tab Buttons -->
            <div class="flex border-b border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 bg-slate-50 dark:bg-slate-800/30 daylight:bg-day-sunken">
              <button class="tab-button active flex-1 sm:flex-auto px-6 py-4 font-semibold text-slate-900 dark:text-white daylight:text-day-ink border-b-2 border-primary flex items-center justify-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 daylight:hover:bg-day-void transition-colors" data-tab="standings">
                <span class="material-symbols-outlined">leaderboard</span>
                <span class="hidden sm:inline">Standings</span>
              </button>
              <button class="tab-button flex-1 sm:flex-auto px-6 py-4 font-semibold text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft border-b-2 border-transparent flex items-center justify-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 daylight:hover:bg-day-void transition-colors" data-tab="rounds">
                <span class="material-symbols-outlined">schedule</span>
                <span class="hidden sm:inline">Rounds</span>
              </button>
              <button class="tab-button flex-1 sm:flex-auto px-6 py-4 font-semibold text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft border-b-2 border-transparent flex items-center justify-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 daylight:hover:bg-day-void transition-colors" data-tab="players">
                <span class="material-symbols-outlined">groups</span>
                <span class="hidden sm:inline">Players</span>
              </button>
              ${
                isManagingOrganizer()
                  ? `
              <button class="tab-button flex-1 sm:flex-auto px-6 py-4 font-semibold text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft border-b-2 border-transparent flex items-center justify-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 daylight:hover:bg-day-void transition-colors" data-tab="insights">
                <span class="material-symbols-outlined">insights</span>
                <span class="hidden sm:inline">Ops Insights</span>
              </button>`
                  : ""
              }
            </div>

            <!-- Tab Content -->
            <div class="p-6 lg:p-8">
              <!-- Standings Tab -->
              <div id="standings-tab" class="tab-content">
                <div class="overflow-x-auto">
                  <table class="w-full text-left text-sm">
                    <thead>
                      <tr class="bg-slate-50 dark:bg-slate-800/50 daylight:bg-day-sunken border-b border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
                        <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">Rank</th>
                        <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">Player/Team</th>
                        <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted text-center">Played</th>
                        <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted text-center">Wins</th>
                        <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted text-center">Win %</th>
                        <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted text-center">Sets Won</th>
                        <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted text-center">Games Won</th>
                        <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted text-center">GD</th>
                        <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted text-right">Points</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-200 dark:divide-slate-800 daylight:divide-day-ink/10" id="standings-tbody">
                      <!-- Standings will be rendered here -->
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Rounds Tab -->
              <div id="rounds-tab" class="tab-content hidden space-y-6">
                <div id="round-actions" class="flex justify-end">
                  <!-- Generate button will be injected here -->
                </div>
                <div id="rounds-list" class="space-y-4">
                  ${renderRoundsList(this.rounds, this.leagueId)}
                </div>
              </div>

              <!-- Players Tab -->
              <div id="players-tab" class="tab-content hidden">
                <div id="enrollment-container"></div>
              </div>

              ${
                isManagingOrganizer()
                  ? `
              <!-- Ops Insights Tab (loaded lazily on first open) -->
              <div id="insights-tab" class="tab-content hidden">
                <div id="insights-content"></div>
              </div>`
                  : ""
              }
            </div>
          </div>
    `;
  }

  private renderStandings(): void {
    const tbody = this.container.querySelector("#standings-tbody");
    if (!tbody) return;

    if (this.standings.length === 0) {
      tbody.innerHTML = EMPTY_STANDINGS_ROW;
      return;
    }

    // Per-player match stats, computed locally for accuracy in individual mode.
    const playerStats = computePlayerStats(this.rounds);

    this.standings.forEach((standing: any, index: number) => {
      const row = document.createElement("tr");
      row.className = "hover:bg-slate-50 dark:hover:bg-slate-800/30 daylight:hover:bg-day-sunken transition-colors";
      row.innerHTML = renderStandingRow(standing, index, playerStats);
      tbody.appendChild(row);
    });
  }

  /**
   * Fetch and render the per-league Ops Insights, once, on first tab open.
   * Cached via `insightsLoaded` so re-clicking the tab doesn't refetch.
   */
  private async loadLeagueInsights(): Promise<void> {
    if (this.insightsLoaded) return;
    const target = this.container.querySelector("#insights-content");
    if (!target) return;

    this.insightsLoaded = true;
    target.innerHTML = renderLeagueInsightsLoading();

    try {
      const analytics = await apiService.getLeagueAnalytics(this.leagueId);
      target.innerHTML = renderLeagueInsights(analytics);
      wireNudgeButtons(target);
    } catch (error) {
      console.error("Failed to load league insights:", error);
      this.insightsLoaded = false; // allow a retry on next open
      target.innerHTML = renderLeagueInsightsError();
    }
  }

  private async checkScheduleEligibility(): Promise<void> {
    const actions = this.container.querySelector("#round-actions");
    if (!actions) return;

    try {
      const eligibility = await apiService.canScheduleRound(this.leagueId);
      if (eligibility.canSchedule) {
        actions.innerHTML = `
          <button id="generate-round-btn" class="btn-primary">
            <span class="material-symbols-outlined">add_circle</span>
            <span>Generate Next Round</span>
          </button>
        `;
        this.container.querySelector("#generate-round-btn")?.addEventListener("click", () => this.generateNextRound());
      } else {
        actions.innerHTML = `
          <div class="flex items-center gap-2 text-sm text-warning daylight:text-day-warning bg-warning/10 daylight:bg-day-warning/[0.18] px-4 py-2 rounded-lg border border-warning/20 daylight:border-day-warning/30">
            <span class="material-symbols-outlined text-base">info</span>
            <span>${eligibility.reason || 'Cannot schedule round'}</span>
          </div>
        `;
      }
    } catch (error) {
      console.error("Error checking schedule eligibility:", error);
    }
  }

  private async generateNextRound(): Promise<void> {
    const btn = this.container.querySelector("#generate-round-btn") as HTMLButtonElement;
    if (btn) {
      btn.disabled = true;
      // Modern inline ring spinner (transform-only animation); `border-current`
      // inherits the button's text colour so it reads in light and dark skins.
      btn.innerHTML = '<span class="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" aria-hidden="true"></span><span>Generating…</span>';
    }

    try {
      const result = await apiService.generateNextRound(this.leagueId);
      const message = result.message || "Next round generated successfully!";
      showNotification(message, "success");
      this.render();
    } catch (error: any) {
      console.error("Failed to generate round:", error);
      const errorMessage = error.message || "Failed to generate round.";
      showNotification(errorMessage, "error");
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = '<span class="material-symbols-outlined">add_circle</span><span>Generate Next Round</span>';
      }
    }
  }

  private getCompletedRounds(): number {
    return this.rounds.filter(r => 
      r.matches && r.matches.length > 0 && r.matches.every(m => m.status === 'reported')
    ).length;
  }

  private getPendingMatches(): number {
    return this.rounds.reduce((total, round) => {
      return total + (round.matches?.filter(m => m.status !== 'reported').length || 0);
    }, 0);
  }

  private setupEventListeners(): void {
    const tabButtons = this.container.querySelectorAll('.tab-button');
    const tabContents = this.container.querySelectorAll('.tab-content');

    tabButtons.forEach(button => {
      button.addEventListener('click', () => {
        const tabName = (button as HTMLElement).getAttribute('data-tab');

        // Update button styles
        tabButtons.forEach(btn => {
          btn.classList.remove('active', 'text-slate-900', 'dark:text-white', 'border-primary');
          btn.classList.add('text-slate-600', 'dark:text-slate-400', 'border-transparent');
        });
        button.classList.add('active', 'text-slate-900', 'dark:text-white', 'border-primary');
        button.classList.remove('text-slate-600', 'dark:text-slate-400', 'border-transparent');

        // Update tab content
        tabContents.forEach(content => {
          const contentId = (content as HTMLElement).id;
          if (contentId === `${tabName}-tab`) {
            content.classList.remove('hidden');
          } else {
            content.classList.add('hidden');
          }
        });

        // Refresh schedule eligibility when switching to rounds tab
        if (tabName === 'rounds') {
          this.checkScheduleEligibility();
        }

        // Load Ops Insights on first open of its tab.
        if (tabName === 'insights') {
          this.loadLeagueInsights();
        }
      });
    });

    // Settings Button Listener
    this.container.querySelector("#league-settings-btn")?.addEventListener("click", () => {
      if (this.league) {
        this.settingsModal.show(this.league);
      }
    });
  }
}
